import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Badge, Container, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";
import { FaArrowLeft, FaEdit, FaTrash, FaUserPlus } from "react-icons/fa";

const SupplierView = () => {
  const { id } = useParams();
  const { pointindex } = useParams();
  const navigate = useNavigate();
  const [supplierData, setSupplierData] = useState({
    companyname: "",
    contactperson: "",
    contactno: "",
    gstno: "",
    udyamnumber: "",
    udyamtype: "",
    email: "",
    packingandforwarding: "",
    paymentterm: '',
    freight: "",
    incoterms: "",
    accountno: "",
    bankname: "",
    bankbranchname: "",
    ifsccode: ""
  });

  const [addresses, setAddresses] = useState([{
    supplieraddress: "",
    country: null,
    state: null,
    city: null,
    statecode: "",
    pincode: "",
  }]);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSupplier = async () => {
      if (id) {
        try {
          const response = await axios.get(`${BASE_URL}supplier/findById/${id}`, {
            headers: authHeader(),
          });
          const data = response.data;

          setSupplierData({
            companyname: data.companyname,
            contactperson: data.contactperson,
            contactno: data.contactno,
            gstno: data.gstno,
            udyamnumber: data.udyamnumber || "",
            udyamtype: data.udyamtype || "",
            email: data.email,
            packingandforwarding: data.packingandforwarding,
            paymentterm: data.paymentterm,
            freight: data.freight,
            incoterms: data.incoterms,
            accountno: data.accountno,
            bankname: data.bankname,
            bankbranchname: data.bankbranchname,
            ifsccode: data.ifsccode
          });

          const supplieraddresses = data.supplierMultipleAddress.map(addr => ({
            supplieraddress: addr.supplieraddress,
            country: addr.country ? { value: addr.country.id, label: addr.country.countryname } : null,
            state: addr.state ? { value: addr.state.id, label: addr.state.statename, code: addr.state.statecode } : null,
            city: addr.city ? { value: addr.city.id, label: addr.city.cityname } : null,
            statecode: addr.statecode,
            pincode: addr.pincode,
          }));

          setAddresses(supplieraddresses);
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to fetch supplier details. Please try again.',
          });
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchSupplier();
  }, [id]);

  const getItem = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(BASE_URL + "supplier/delete/" + id, {
          headers: authHeader(),
        })
          .then(response => {
            if (response.data.length === 0) {
              Swal.fire(
                'Warning',
                'Supplier present in another module. Delete operation has been cancelled.',
                'warning'
              );
            } else {
              navigate(`/supplierindex/${pointindex}`);
              Swal.fire(
                'Deleted!',
                'Supplier deleted successfully.',
                'success'
              );
            }
          })
          .catch((error) => {
            Swal.fire(
              'Error',
              error.response.data,
              'error'
            );
          });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="p-3">
      {/* Header Section */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold text-success mb-0">SUPPLIER - VIEW</h5>
            <div>
              <Link to={`/contactSTable/${id}/${pointindex}`} className="btn btn-outline-primary btn-sm me-2">
                <FaUserPlus className="me-1" />
                Add Contact
              </Link>
              <Button variant="outline-danger" size="sm" className="me-2" onClick={() => getItem(id)}>
                <FaTrash className="me-1" />
                Delete
              </Button>
              <Link to={`/supplierEdit/${id}/${pointindex}`} className="btn btn-outline-warning btn-sm me-2">
                <FaEdit className="me-1" />
                Edit
              </Link>
              <Link to={`/supplierindex/${pointindex}`} className="btn btn-secondary btn-sm">
                <FaArrowLeft className="me-1" />
                Back
              </Link>
            </div>
          </div>

          <Row className="g-2">
            <Col md={3}>
              <div className="border-start border-3 border-success ps-2">
                <small className="text-muted">Company Name</small>
                <div className="fw-bold">{supplierData.companyname || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-primary ps-2">
                <small className="text-muted">Contact Person</small>
                <div className="fw-bold">{supplierData.contactperson || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-warning ps-2">
                <small className="text-muted">GST No</small>
                <div className="fw-bold">{supplierData.gstno || '-'}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-start border-3 border-info ps-2">
                <small className="text-muted">Status</small>
                <div>
                  <Badge bg="success" className="px-2 py-1">Active</Badge>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Basic Information */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Basic Information</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">Company Name</small>
              <div className="fw-bold">{supplierData.companyname || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Contact Person</small>
              <div className="fw-bold">{supplierData.contactperson || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Mobile No</small>
              <div className="fw-bold">{supplierData.contactno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Email</small>
              <div className="fw-bold">{supplierData.email || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">GST No</small>
              <div className="fw-bold">{supplierData.gstno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Udyam Number</small>
              <div className="fw-bold">{supplierData.udyamnumber || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Udyam Type</small>
              <div className="fw-bold">{supplierData.udyamtype || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Pricing Terms */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Pricing Terms</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={3}>
              <small className="text-muted">Packing & Forwarding (%)</small>
              <div className="fw-bold">{supplierData.packingandforwarding || '0'}%</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">Freight (%)</small>
              <div className="fw-bold">{supplierData.freight || '0'}%</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">Payment Terms</small>
              <div className="fw-bold">{supplierData.paymentterm || '-'}</div>
            </Col>
            <Col md={3}>
              <small className="text-muted">Inco Terms</small>
              <div className="fw-bold">{supplierData.incoterms || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Bank Details */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Bank Details</h6>
        </div>
        <div className="p-3">
          <Row className="g-3">
            <Col md={4}>
              <small className="text-muted">Bank Name</small>
              <div className="fw-bold">{supplierData.bankname || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Account No</small>
              <div className="fw-bold">{supplierData.accountno || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Branch Name</small>
              <div className="fw-bold">{supplierData.bankbranchname || '-'}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">IFSC Code</small>
              <div className="fw-bold">{supplierData.ifsccode || '-'}</div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Address Details */}
      <div className="shadow-sm border rounded bg-white mb-3">
        <div className="bg-light py-2 px-3 border-bottom">
          <h6 className="mb-0 fw-bold">Address Details</h6>
        </div>
        <div className="p-3">
          {addresses.map((addr, index) => (
            <div key={index} className={index > 0 ? 'mt-3 pt-3 border-top' : ''}>
              <Badge bg="secondary" className="mb-3 px-3 py-2">
                Address #{index + 1}
              </Badge>
              <Row className="g-3">
                <Col md={12}>
                  <small className="text-muted">Address Line</small>
                  <div className="fw-bold">{addr.supplieraddress || '-'}</div>
                </Col>
                <Col md={3}>
                  <small className="text-muted">Country</small>
                  <div className="fw-bold">{addr.country?.label || '-'}</div>
                </Col>
                <Col md={3}>
                  <small className="text-muted">State</small>
                  <div className="fw-bold">{addr.state?.label || '-'}</div>
                </Col>
                <Col md={3}>
                  <small className="text-muted">State Code</small>
                  <div className="fw-bold">{addr.statecode || '-'}</div>
                </Col>
                <Col md={3}>
                  <small className="text-muted">City</small>
                  <div className="fw-bold">{addr.city?.label || '-'}</div>
                </Col>
                <Col md={3}>
                  <small className="text-muted">Pincode</small>
                  <div className="fw-bold">{addr.pincode || '-'}</div>
                </Col>
              </Row>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="text-center mt-3 pt-2 border-top">
        <Link to={`/supplierEdit/${id}/${pointindex}`} className="btn btn-success btn-sm me-2">
          <FaEdit className="me-1" />
          Edit Supplier
        </Link>
        <Link to={`/supplierindex/${pointindex}`} className="btn btn-secondary btn-sm">
          <FaArrowLeft className="me-1" />
          Back to List
        </Link>
      </div>
    </Container>
  );
};

export default SupplierView;